/**
 * „Wohin?“ -- die eine Zielauswahl für Verschieben, Ausbuchen, Mehrfach und
 * Stammplatz (ADR 0013 §4).
 *
 * Von oben nach unten, vom schnellsten zum langsamsten Weg:
 *
 * 1. **Ziel scannen** -- das Etikett am Fach oder an der Kiste,
 * 2. die **letzten vier Ziele** dieses Benutzers,
 * 3. **Suche** über Lagerorte, Packstücke und Einsatzstellen,
 * 4. der **Baum** zum Blättern,
 * 5. **„+ Neues Packstück“** -- nur mit Netz, das neue wird gleich gewählt
 *    (ADR 0016).
 *
 * Was nicht geht, steht mit Grund da, ohne Knopf: Ein Packstück passt nicht in
 * sich selbst. Alles kommt aus der lokalen Kopie und geht offline. Selbst
 * geöffnet und jederzeit mit × zu verlassen -- kein Dialog, der etwas
 * erzwingt (CLAUDE.md §7).
 */

import { useMemo, useState } from "react";
import type { Lagerort } from "@thw/contracts";
import type { Ziel } from "@thw/core-domain";
import { sitzungLesen } from "../api/klient.js";
import { useOnline } from "../api/online.js";
import { bestandLaden, useBestand, type Bestand } from "../lokal/bestand.js";
import { vorschlaegeFuer } from "../lokal/neuesPackstueck.js";
import { packstueckStandort } from "../lokal/packstueck.js";
import { vereinheitlichen } from "../lokal/suche.js";
import {
  ortDesArtikels,
  schonDort,
  standardziel,
  uebergeordnet,
  unterorte,
  verboten,
  zielGueltig,
} from "../lokal/verschieben.js";
import { gleichesZiel, LETZTE_ZIELE, zieleLesen, zielMerken } from "../lokal/ziele.js";
import { aufloesenIn, codeAufloesen } from "../scan/aufloesen.js";
import { Scanner, rueckmeldungGeben } from "../scan/Scanner.jsx";
import { Knopf, Ladeanzeige, Rueckmeldung, Schalter } from "./bausteine.jsx";
import { NeuesPackstueck } from "./NeuesPackstueck.jsx";

/** Mehr Treffer liest an der Einsatzstelle niemand. */
const HOECHSTENS = 15;

export interface WohinEigenschaften {
  /** Überschrift, etwa „Wohin? — GB-01 verschieben“. */
  titel?: string;
  /** Was bewegt wird. Dann fehlen Ziele, an denen es schon ist oder die es selbst enthält. */
  artikel_id?: string | null;
  /** Weitere Ziele, die nicht in Frage kommen, mit Grund. */
  ausschliessen?: (ziel: Ziel) => string | null;
  /** Das Ziel zugleich als Vorbelegung fürs Ausbuchen merken. */
  alsAusbuchenZiel?: boolean;
  /**
   * Wofür gewählt wird. Beim **Stammplatz** (ADR 0013 §6) gibt es keine
   * Einsatzstellen und nicht „Ohne festen Platz“, und der Ort, an dem der
   * Artikel gerade liegt, ist gerade recht -- dort festlegen ist der häufigste
   * Fall. Beim **Standort** eines neuen Packstücks (ADR 0016) gibt es keine
   * Einsatzstellen und kein „+ Neues Packstück“.
   */
  zweck?: "ziel" | "stammplatz" | "standort";
  /**
   * Wo ein neues Packstück aus „+ Neues Packstück“ stehen soll -- vorbelegt,
   * etwa der Behälter, von dem man kommt. Ohne Angabe der Ort des Artikels.
   */
  vorschlagOrt?: string | null;
  /**
   * Der Haken „Als neuen Stammplatz setzen“ beim Verschieben (Rueckmeldungen
   * 4.3). Anfangs aus, braucht Netz, gilt nur fuer einen festen Ort oder ein
   * Packstueck -- nicht fuer Einsatzstellen und nicht „Ohne festen Platz“.
   */
  stammplatzAnbieten?: boolean;
  /** `alsStammplatz` ist nur wahr, wenn der Haken gesetzt ist und gelten kann. */
  aufWahl: (ziel: Ziel, alsStammplatz: boolean) => void;
  aufAbbruch: () => void;
}

const EBENE_TEXT: Record<Lagerort["ebene"], string> = {
  ort: "Ort",
  unterebene: "Fach",
  packstueck: "Packstück",
};

/** „in LKW-1“, „im Einsatz — Pumpenstelle Nord“ -- wo ein Lagerort gerade steckt. */
export function ortZusatz(bestand: Bestand, lagerort: Lagerort): string | null {
  if (lagerort.artikel_id !== null) {
    const standort = packstueckStandort(bestand, lagerort.id);
    if (standort !== null) return standort;
  }
  const oben = uebergeordnet(bestand, lagerort);
  return oben === null ? null : `in ${oben.kennung}`;
}

interface Eintrag {
  ziel: Ziel;
  name: string;
  art: string;
  zusatz: string | null;
}

function eintragFuer(bestand: Bestand, ziel: Ziel): Eintrag | null {
  if (ziel.art === "einsatzstelle") {
    const stelle = bestand.einsatzstellen.get(ziel.id);
    return stelle === undefined
      ? null
      : { ziel, name: stelle.bezeichnung, art: "Einsatz", zusatz: null };
  }
  const l = bestand.lagerorte.get(ziel.id);
  if (l === undefined) return null;
  return {
    ziel,
    name: l.standardziel || l.unbekannt ? l.bezeichnung : `${l.kennung} — ${l.bezeichnung}`,
    art: EBENE_TEXT[l.ebene],
    zusatz: ortZusatz(bestand, l),
  };
}

function suchen(bestand: Bestand, eingabe: string, mitEinsatzstellen: boolean): Eintrag[] {
  const woerter = vereinheitlichen(eingabe.trim()).split(/\s+/).filter(Boolean);
  if (woerter.length === 0) return [];
  const passt = (text: string): boolean => {
    const t = vereinheitlichen(text);
    return woerter.every((w) => t.includes(w));
  };

  const orte = [...bestand.lagerorte.values()]
    // „Unbekannt“ ist kein Ziel von Hand (ADR 0020) -- in der Suche gar nicht erst.
    .filter((l) => l.aktiv && !l.unbekannt && passt(`${l.kennung} ${l.bezeichnung}`))
    .sort((a, b) => {
      // Wer die Kennung anfängt, meint sie.
      const vorn = (l: Lagerort): number =>
        vereinheitlichen(l.kennung).startsWith(woerter[0] ?? "") ? 0 : 1;
      return vorn(a) - vorn(b) || a.kennung.localeCompare(b.kennung, "de");
    })
    .map((l): Ziel => ({ art: "lagerort", id: l.id }));
  const stellen = [...bestand.einsatzstellen.values()]
    .filter((e) => mitEinsatzstellen && e.status === "aktiv" && passt(e.bezeichnung))
    .map((e): Ziel => ({ art: "einsatzstelle", id: e.id }));

  return [...orte, ...stellen]
    .slice(0, HOECHSTENS)
    .map((z) => eintragFuer(bestand, z))
    .filter((e) => e !== null);
}

export function Wohin({
  titel = "Wohin?",
  artikel_id = null,
  ausschliessen,
  alsAusbuchenZiel = false,
  zweck = "ziel",
  vorschlagOrt,
  stammplatzAnbieten = false,
  aufWahl,
  aufAbbruch,
}: WohinEigenschaften) {
  const bestand = useBestand();
  const benutzer_id = sitzungLesen()?.benutzer.id ?? "";
  const [scannen, setScannen] = useState(false);
  const [alsStammplatz, setAlsStammplatz] = useState(false);
  const online = useOnline();
  const [meldung, setMeldung] = useState<string | null>(null);
  const [eingabe, setEingabe] = useState("");
  const [pfad, setPfad] = useState<string[]>([]);

  const stammplatz = zweck === "stammplatz";
  /** Beim Stammplatz und beim Standort zählen nur Lagerorte. */
  const nurLagerorte = zweck !== "ziel";
  const [neu, setNeu] = useState(false);

  /** Warum ein Ziel nicht geht -- oder `null`, wenn es geht. */
  const grund = (ziel: Ziel): string | null => {
    if (bestand === undefined) return null;
    if (nurLagerorte && ziel.art === "einsatzstelle") {
      return stammplatz ? "kein Stammplatz" : "kein Lagerort";
    }
    if (stammplatz && bestand.lagerorte.get(ziel.id)?.standardziel === true) {
      return "kein Stammplatz";
    }
    // Dorthin bucht nur der Status „verloren“ oder „gestohlen“ (ADR 0020).
    if (ziel.art === "lagerort" && bestand.lagerorte.get(ziel.id)?.unbekannt === true) {
      return "nur über den Status verloren oder gestohlen";
    }
    if (artikel_id !== null) {
      if (verboten(bestand, artikel_id, ziel)) {
        return "geht nicht: das Packstück selbst oder darin";
      }
      if (!stammplatz && schonDort(bestand.zustaende.get(artikel_id), ziel)) {
        return "ist schon hier";
      }
    }
    return ausschliessen?.(ziel) ?? null;
  };

  const waehlen = (ziel: Ziel): void => {
    zielMerken(benutzer_id, ziel, alsAusbuchenZiel);
    const taugt =
      ziel.art === "lagerort" && bestand?.lagerorte.get(ziel.id)?.standardziel === false;
    aufWahl(ziel, stammplatzAnbieten && alsStammplatz && online && taugt);
  };

  const letzte = useMemo(() => {
    if (bestand === undefined) return [];
    const liste = zieleLesen(benutzer_id).letzte.filter(
      (z) =>
        zielGueltig(bestand, z) &&
        // Als Stammplatz taugen weder Einsatzstellen noch „Ohne festen Platz“,
        // als Standort keine Einsatzstelle.
        !(nurLagerorte && z.art === "einsatzstelle") &&
        !(stammplatz && z.art === "lagerort" && bestand.lagerorte.get(z.id)?.standardziel === true),
    );
    // Beim ersten Mal -- und solange Platz ist -- steht das Standardziel dabei.
    const standard = standardziel(bestand);
    if (!stammplatz && standard !== null && liste.length < LETZTE_ZIELE) {
      const ziel: Ziel = { art: "lagerort", id: standard.id };
      if (!liste.some((z) => gleichesZiel(z, ziel))) liste.push(ziel);
    }
    return liste.map((z) => eintragFuer(bestand, z)).filter((e) => e !== null);
  }, [bestand, benutzer_id, stammplatz, nurLagerorte]);

  const treffer = useMemo(
    () => (bestand === undefined ? [] : suchen(bestand, eingabe, !nurLagerorte)),
    [bestand, eingabe, nurLagerorte],
  );

  const aufCode = async (code: string): Promise<void> => {
    let b = await bestandLaden();
    let gefunden = aufloesenIn(b, code);
    if (gefunden.art === "unbekannt") {
      gefunden = await codeAufloesen(code);
      b = await bestandLaden();
    }

    let ziel: Ziel | null = null;
    if (gefunden.art === "lagerort") ziel = { art: "lagerort", id: gefunden.lagerort.id };
    if (gefunden.art === "artikel") {
      // Das Etikett eines Packstücks trägt manchmal die Inventarnummer.
      const ort = ortDesArtikels(b, gefunden.artikel.id);
      if (ort !== null) ziel = { art: "lagerort", id: ort.id };
    }

    if (ziel === null) {
      rueckmeldungGeben(false);
      setMeldung(
        gefunden.art === "artikel"
          ? `${gefunden.artikel.bezeichnung} ist ein Artikel, kein Ziel. Das Etikett am Fach oder an der Kiste scannen — oder unten suchen.`
          : `Der Code „${code}“ ist kein bekanntes Ziel. Unten suchen oder im Baum blättern.`,
      );
      return;
    }
    const warum = grund(ziel);
    if (warum !== null) {
      rueckmeldungGeben(false);
      setMeldung(`${eintragFuer(b, ziel)?.name ?? "Dieses Ziel"}: ${warum}`);
      return;
    }
    rueckmeldungGeben(true);
    waehlen(ziel);
  };

  const hier = pfad.at(-1) ?? null;
  const hierOrt = hier === null || bestand === undefined ? null : bestand.lagerorte.get(hier);

  if (neu && bestand !== undefined) {
    // Steht der Artikel an einem Ort, kommt die neue Kiste dorthin -- sonst
    // wählt man den Standort selbst.
    const ort =
      vorschlagOrt !== undefined
        ? vorschlagOrt
        : artikel_id === null
          ? null
          : (bestand.zustaende.get(artikel_id)?.lagerort_id ?? null);
    return (
      <NeuesPackstueck
        ort_id={ort}
        weiter={stammplatz ? "Als Stammplatz nehmen" : "Als Ziel nehmen"}
        aufAbbruch={() => {
          setNeu(false);
        }}
        aufFertig={(lagerort) => {
          setNeu(false);
          void bestandLaden().then((b) => {
            const ziel: Ziel = { art: "lagerort", id: lagerort.id };
            // Frisch gelesen: Die neue Kiste muss schon in der Rechnung sein,
            // sonst sähe „in sich selbst“ sie nicht.
            const warum =
              artikel_id !== null && verboten(b, artikel_id, ziel)
                ? "geht nicht: das Packstück selbst oder darin"
                : (ausschliessen?.(ziel) ?? null);
            if (warum === null) waehlen(ziel);
            else setMeldung(`${lagerort.kennung}: ${warum}`);
          });
        }}
      />
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Wohin?"
      className="fixed inset-0 z-30 overflow-y-auto bg-neutral-100 text-neutral-900 dark:bg-neutral-950 dark:text-neutral-50"
    >
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-blue-800 px-4 py-2 text-white">
        <h2 className="text-lg font-bold">{titel}</h2>
        <button
          type="button"
          aria-label="Schließen"
          onClick={aufAbbruch}
          className="flex min-h-12 min-w-12 items-center justify-center rounded-lg text-2xl font-bold active:bg-blue-900"
        >
          ×
        </button>
      </header>

      {bestand === undefined ? (
        <Ladeanzeige was="Die Ziele" />
      ) : (
        <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 pb-24">
          <Knopf
            art={scannen ? "neben" : "haupt"}
            onClick={() => {
              setScannen(!scannen);
              setMeldung(null);
            }}
          >
            {scannen ? "Scannen beenden" : "Ziel scannen"}
          </Knopf>
          {stammplatzAnbieten && (
            <Schalter
              beschriftung="Als neuen Stammplatz setzen"
              hinweis={
                online
                  ? "Nur bei einem festen Ort oder Packstück."
                  : "Braucht Netz. Verschieben geht auch so."
              }
              an={alsStammplatz && online}
              gesperrt={!online}
              aendern={setAlsStammplatz}
            />
          )}
          {meldung !== null && <Rueckmeldung art="warnung" kopf="Kein Ziel" zusatz={meldung} />}
          {scannen && <Scanner aufCode={(code) => void aufCode(code)} />}

          {letzte.length > 0 && (
            <section aria-label="Zuletzt">
              <h3 className="mb-2 text-sm font-semibold text-neutral-500">Zuletzt</h3>
              <div className="flex flex-wrap gap-2">
                {letzte.map((e) => {
                  const warum = grund(e.ziel);
                  return (
                    <button
                      key={`${e.ziel.art}-${e.ziel.id}`}
                      type="button"
                      disabled={warum !== null}
                      title={warum ?? undefined}
                      onClick={() => {
                        waehlen(e.ziel);
                      }}
                      className="min-h-12 rounded-full bg-blue-100 px-4 font-semibold text-blue-950 active:bg-blue-200 disabled:opacity-40 dark:bg-blue-950 dark:text-blue-100"
                    >
                      {e.ziel.art === "lagerort" ? zielKurz(bestand, e.ziel.id) : e.name}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section aria-label="Suche" className="flex flex-col gap-2">
            <input
              className="min-h-12 rounded-xl border-2 border-neutral-300 bg-white px-4 text-base dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
              value={eingabe}
              onChange={(e) => {
                setEingabe(e.target.value);
              }}
              placeholder={nurLagerorte ? "Ort oder Packstück" : "Ort, Packstück, Einsatzstelle"}
              aria-label="Ziel suchen"
              autoComplete="off"
              enterKeyHint="search"
            />
            {eingabe.trim() !== "" && treffer.length === 0 && (
              <p className="text-sm text-neutral-600 dark:text-neutral-300">Nichts gefunden.</p>
            )}
            {treffer.length > 0 && (
              <ul className="flex flex-col gap-1">
                {treffer.map((e) => (
                  <li key={`${e.ziel.art}-${e.ziel.id}`}>
                    <ZielZeile eintrag={e} grund={grund(e.ziel)} aufWahl={waehlen} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          {eingabe.trim() === "" && (
            <section aria-label="Baum" className="flex flex-col gap-1">
              <h3 className="mb-1 text-sm font-semibold text-neutral-500">
                {hierOrt === null || hierOrt === undefined
                  ? "Alle Orte"
                  : `In ${hierOrt.kennung} — ${hierOrt.bezeichnung}`}
              </h3>
              {hier !== null && (
                <Knopf
                  art="still"
                  onClick={() => {
                    setPfad(pfad.slice(0, -1));
                  }}
                >
                  ‹ zurück
                </Knopf>
              )}
              <ul className="flex flex-col gap-1">
                {unterorte(bestand, hier).map((l) => {
                  const e = eintragFuer(bestand, { art: "lagerort", id: l.id });
                  if (e === null) return null;
                  const tiefer = unterorte(bestand, l.id).length > 0;
                  return (
                    <li key={l.id} className="flex gap-1">
                      <div className="min-w-0 flex-1">
                        <ZielZeile eintrag={e} grund={grund(e.ziel)} aufWahl={waehlen} />
                      </div>
                      {tiefer && (
                        <button
                          type="button"
                          aria-label={`${l.kennung} öffnen`}
                          onClick={() => {
                            setPfad([...pfad, l.id]);
                          }}
                          className="min-h-12 min-w-12 rounded-xl bg-neutral-200 text-xl font-bold active:bg-neutral-300 dark:bg-neutral-700"
                        >
                          ›
                        </button>
                      )}
                    </li>
                  );
                })}
                {hier === null &&
                  !nurLagerorte &&
                  [...bestand.einsatzstellen.values()]
                    .filter((s) => s.status === "aktiv")
                    .map((s) => {
                      const e = eintragFuer(bestand, { art: "einsatzstelle", id: s.id });
                      return e === null ? null : (
                        <li key={s.id}>
                          <ZielZeile eintrag={e} grund={grund(e.ziel)} aufWahl={waehlen} />
                        </li>
                      );
                    })}
              </ul>
            </section>
          )}

          {zweck !== "standort" && (
            <NeuesPackstueckKnopf
              bestand={bestand}
              aufTippen={() => {
                setNeu(true);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Der Name auf einem Chip: kurz, damit vier in eine Zeile passen. */
function zielKurz(bestand: Bestand, id: string): string {
  const l = bestand.lagerorte.get(id);
  if (l === undefined) return "?";
  return l.standardziel || l.unbekannt ? l.bezeichnung : l.kennung;
}

function ZielZeile({
  eintrag,
  grund,
  aufWahl,
}: {
  eintrag: Eintrag;
  grund: string | null;
  aufWahl: (ziel: Ziel) => void;
}) {
  const inhalt = (
    <>
      <span className="font-semibold">{eintrag.name}</span>
      <span className="text-sm text-neutral-600 dark:text-neutral-300">
        {eintrag.art}
        {eintrag.zusatz !== null && ` · ${eintrag.zusatz}`}
        {grund !== null && ` · ${grund}`}
      </span>
    </>
  );
  if (grund !== null) {
    return (
      <div className="flex min-h-12 flex-col justify-center rounded-xl bg-neutral-200/60 px-4 py-2 opacity-70 dark:bg-neutral-800/60">
        {inhalt}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        aufWahl(eintrag.ziel);
      }}
      className="flex min-h-12 w-full flex-col justify-center rounded-xl bg-white px-4 py-2 text-left active:bg-neutral-200 dark:bg-neutral-800 dark:active:bg-neutral-700"
    >
      {inhalt}
    </button>
  );
}

/**
 * „+ Neues Packstück · GB-09“ (ADR 0013 §4, §7) -- am Ende der Auswahl. Ohne
 * Netz gesperrt, mit dem Grund.
 */
function NeuesPackstueckKnopf({ bestand, aufTippen }: { bestand: Bestand; aufTippen: () => void }) {
  const online = useOnline();
  const vorschlag = useMemo(() => vorschlaegeFuer(bestand)[0]?.kennung ?? null, [bestand]);
  return (
    <button
      type="button"
      disabled={!online}
      onClick={aufTippen}
      className="flex min-h-14 w-full flex-col justify-center rounded-xl border-2 border-dashed border-blue-700 px-4 py-2 text-left text-blue-900 active:bg-blue-50 disabled:opacity-50 dark:border-blue-400 dark:text-blue-100 dark:active:bg-blue-950"
    >
      <span className="font-semibold">
        + Neues Packstück{vorschlag === null ? "" : ` · ${vorschlag}`}
      </span>
      <span className="text-sm">
        {online ? "Neu anlegen nur mit Netz" : "Ohne Netz gesperrt — neu anlegen geht nur mit Netz"}
      </span>
    </button>
  );
}
